
import React, { useState } from 'react';
import { VendorProfile, Neighborhood, Category, VendorStatus, SubscriptionPlan } from '../types';

interface PreviewModalProps {
  vendor: VendorProfile;
  neighborhood: Neighborhood | undefined;
  category: Category | undefined;
  categories: Category[];
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onRequestChange: (vendor: VendorProfile) => void;
  onUpdatePlan: (id: string, plan: SubscriptionPlan, days: number) => void;
  onGrantBadge: (days: number) => void; 
  onUpdateCategory: (vendorId: string, categoryId: string) => void;
}

const VendorPreviewModal: React.FC<PreviewModalProps> = ({ 
  vendor, neighborhood, category, categories, onClose, onApprove, onReject, onRequestChange, onUpdatePlan, onGrantBadge, onUpdateCategory 
}) => {
  const [showConfirmReject, setShowConfirmReject] = useState(false);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  
  const isBadgeActive = vendor.verified_until ? new Date(vendor.verified_until) > new Date() : false;
  const isApproved = vendor.status === VendorStatus.APPROVED;
  const isPending = vendor.status === VendorStatus.PENDING;
  const isPro = vendor.plan === SubscriptionPlan.PRO;

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return '';
    }
  };

  const handleCategoryChange = async (newId: string) => {
    setIsUpdatingCategory(true);
    await onUpdateCategory(vendor.id, newId);
    setIsUpdatingCategory(false);
  };

  const handleExecuteReject = () => {
    onReject(vendor.id);
    setShowConfirmReject(false);
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy/95 backdrop-blur-xl" onClick={onClose} />
      
      {showConfirmReject && (
        <div className="absolute inset-0 z-[130] flex items-center justify-center p-6 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-navy/80 backdrop-blur-sm" />
          <div className="relative bg-white w-full max-w-sm rounded-[3rem] p-10 text-center shadow-2xl animate-in zoom-in duration-300">
            <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
            </div>
            <h3 className="text-xl font-title font-black text-navy uppercase italic mb-3 tracking-tighter">Confirmar Pausa?</h3>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-8 leading-relaxed">Iso tornará a loja invisível para todos os usuários.</p>
            <div className="space-y-3">
              <button onClick={handleExecuteReject} className="w-full bg-red-500 text-white font-black py-5 rounded-2xl shadow-xl shadow-red-500/20 uppercase tracking-widest text-[9px]">Sim, Pausar Loja</button>
              <button onClick={() => setShowConfirmReject(false)} className="w-full bg-gray-100 text-gray-400 font-black py-5 rounded-2xl uppercase tracking-widest text-[9px]">Manter no Ar</button>
            </div>
          </div>
        </div>
      )}

      <div className="relative bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300 max-h-[95vh] flex flex-col">
        <div className="p-10 overflow-y-auto no-scrollbar">
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-center gap-6">
              <div className="relative">
                <img src={vendor.avatar_url} className="w-24 h-24 rounded-[2rem] object-cover border-4 border-gray-50 shadow-lg" alt="" />
                {(isBadgeActive || isPro) && (
                  <div className="absolute -top-2 -right-2 bg-brand-orange p-1.5 rounded-full border-4 border-white shadow-lg">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/></svg>
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-3xl font-title font-black text-navy uppercase tracking-tighter italic leading-none mb-2">{vendor.name}</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <p className={`text-[10px] font-black px-3 py-1 rounded-full uppercase inline-block shadow-sm ${isApproved ? 'bg-green-500 text-white' : vendor.status === VendorStatus.PENDING ? 'bg-brand-orange text-white' : 'bg-red-500 text-white'}`}>
                     {isApproved ? 'Loja Operante ✓' : vendor.status === VendorStatus.PENDING ? 'Em Análise Moderada' : 'Loja Pausada'}
                  </p>
                  <p className={`text-[10px] font-black px-3 py-1 rounded-full uppercase inline-block shadow-sm ${isPro ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-400'}`}>
                     {isPro ? '💎 Plano PRO' : 'Plano FREE'}
                  </p>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-300 hover:text-navy transition-colors">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 border-t border-gray-50 pt-8">
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Responsável</label>
                <p className="font-bold text-navy">{vendor.responsible}</p>
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Localização</label>
                <p className="font-bold text-navy">📍 {neighborhood?.name}</p>
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">WhatsApp</label>
                <a href={`https://wa.me/${vendor.whatsapp}`} target="_blank" className="font-bold text-brand-blue hover:underline flex items-center gap-1">
                   {vendor.whatsapp} <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                </a>
              </div>
            </div>
            <div className="space-y-4">
               <div className="relative">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Segmento do Parceiro</label>
                <div className="relative group/select">
                  <select 
                    disabled={isUpdatingCategory}
                    className="w-full bg-gray-50 border-none rounded-xl p-3 font-bold text-navy text-xs tracking-tighter appearance-none cursor-pointer focus:ring-2 focus:ring-brand-orange pr-10 disabled:opacity-50"
                    value={vendor.category_id}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-300">
                    {isUpdatingCategory ? (
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">E-mail de Cadastro</label>
                <p className="font-bold text-navy text-xs">{vendor.email}</p>
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Bio/Descrição</label>
                <p className="text-xs text-gray-500 italic leading-tight">"{vendor.bio.replace(/VCODE:.*\|/, '')}"</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            <div className={`p-6 rounded-[2.5rem] border transition-all ${isBadgeActive || isPro ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100'}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-[9px] font-black text-navy uppercase tracking-widest flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${isBadgeActive || isPro ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></span>
                  Selo Verificado
                </h4>
                {isPro && <span className="bg-brand-orange text-white text-[7px] font-black px-2 py-0.5 rounded-lg uppercase">Pro Benefits</span>}
              </div>

              <div className="mb-4">
                {isPro ? (
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-green-700 uppercase italic">Ativo via Plano PRO</p>
                    <p className="text-[8px] text-gray-400 font-bold uppercase tracking-tight">Expira com a assinatura</p>
                  </div>
                ) : isBadgeActive ? (
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-green-600 uppercase tracking-widest">Ativo até {formatDate(vendor.verified_until)}</p>
                    <p className="text-[8px] text-gray-400 font-bold uppercase tracking-tight">Selo Promocional Ativo</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Selo Inativo</p>
                    <p className="text-[8px] text-gray-300 font-bold uppercase tracking-tight">Sem destaque promocional</p>
                  </div>
                )}
              </div>

              {!isPro && (
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => onGrantBadge(7)} className="bg-white border border-gray-200 text-navy font-black text-[8px] py-2.5 rounded-xl hover:border-brand-orange transition-all">+7d</button>
                  <button onClick={() => onGrantBadge(30)} className="bg-white border border-gray-200 text-navy font-black text-[8px] py-2.5 rounded-xl hover:border-brand-orange transition-all">+30d</button>
                  <button onClick={() => onGrantBadge(0)} className="bg-red-50 text-red-500 font-black text-[8px] py-2.5 rounded-xl hover:bg-red-500 hover:text-white transition-all">LIMPAR</button>
                </div>
              )}
            </div>

            <div className="bg-brand-navy p-6 rounded-[2.5rem] border border-white/5 shadow-xl">
               <h4 className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${isPro ? 'bg-brand-orange animate-pulse' : 'bg-white/20'}`}></span>
                Assinatura PRO
              </h4>
              
              <div className="mb-4">
                {isPro ? (
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-brand-orange uppercase tracking-widest">Vence em {formatDate(vendor.plan_expires_at)}</p>
                    <p className="text-[8px] text-white/20 font-bold uppercase tracking-tight">Catálogo Ilimitado Ativo</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-widest">Plano Grátis</p>
                    <p className="text-[8px] text-white/20 font-bold uppercase tracking-tight">Limite de anúncios ativo</p>
                  </div>
                )}
              </div>

              {isPro ? (
                <button 
                  onClick={() => onUpdatePlan(vendor.id, SubscriptionPlan.FREE, 0)}
                  className="w-full bg-white/5 border border-white/10 text-white/60 hover:text-red-400 hover:border-red-400 font-black text-[9px] py-3 rounded-xl transition-all uppercase tracking-widest"
                >
                  Cancelar PRO
                </button>
              ) : (
                <button 
                  onClick={() => onUpdatePlan(vendor.id, SubscriptionPlan.PRO, 30)}
                  className="w-full bg-brand-orange text-white font-black text-[9px] py-3 rounded-xl shadow-lg hover:scale-105 transition-all uppercase tracking-widest"
                >
                  Ativar PRO (30d)
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            {isPending && (
              <button 
                onClick={() => onRequestChange(vendor)}
                className="flex-1 bg-yellow-50 text-yellow-600 font-black py-5 rounded-2xl transition-all uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 shadow-sm hover:bg-yellow-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                Solicitar Ajustes
              </button>
            )}

            {isApproved ? (
              <button 
                onClick={() => setShowConfirmReject(true)}
                className="flex-1 bg-red-50 text-red-500 font-black py-5 rounded-2xl transition-all uppercase tracking-widest text-[10px]"
              >
                Suspender / Pausar
              </button>
            ) : (
              <button 
                onClick={() => onApprove(vendor.id)}
                className="flex-1 bg-green-500 text-white font-black py-5 rounded-2xl shadow-xl shadow-green-500/20 hover:scale-[1.02] transition-all uppercase tracking-widest text-[10px]"
              >
                Aprovar Operação
              </button>
            )}
            
            <button onClick={onClose} className="px-8 bg-gray-100 text-gray-400 font-black py-5 rounded-2xl hover:bg-gray-200 transition-all uppercase tracking-widest text-[10px]">Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorPreviewModal;
